import { STUDIO_NAV_FEATURE_FLAG_KEYS } from '@/@studio/types/constants';
import { isProduction as isAppEnvProduction } from '@/lib/config/env';
import { FLEET_BALANCE_FLAG } from '@/lib/credits/fleet-flag';
import { CROSSTABS_DANI_LAUNCH_FLAG } from '@/lib/crosstab/dani/launch-flag';
import { LEGACY_CROSSTABS_RETIRED_FLAG } from '@/lib/crosstab/legacy-retirement';
import { SLIDE_CREATOR_ALPHA_FLAG } from '@/lib/slide-creation/alpha-access';
import { STAFF_TOOL_FLAGS } from '@/lib/staff-tools/tools';

/**
 * Register feature flag keys here as they are created on the backend.
 * These are fetched in bulk on app load via the FeatureFlagsProvider.
 */
export const FEATURE_FLAG_KEYS: string[] = [
    'studio-ai-planner-mode',
    'credit-consumption',
    'studio-project-refresh',
    'iooh-brand-lift',
    // Ad Network Intelligence (M4S-5759): gates the Board Reports card to OOH
    // networks whose media data we hold. The DANI-team row carries no flag yet,
    // so the card resolves by name until the row is flagged — see
    // docs/pdlc/reports-ad-network-intelligence.md.
    'reports-ad-network-intelligence',
    // LLM Signals held back (M4S-5700 D7). ON turns the card back to "Coming
    // soon" for that user; off, unset or unreadable leaves it live. It gates the
    // card only, never the plan-template row. Read through `reportStatus`, and
    // server-side on the build page.
    'reports-llm-signals-coming-soon',
    // No key for Survey Analysis, iOOH Mid-Flight, Customer Leakage, Cross
    // Shopping or LLM Signals: all five are open to every user and their
    // plan-template rows carry no flag, so their cards resolve by exact
    // template name instead; the LLM Signals key above holds back its card, not
    // its row. (`reports-cross-shopping` was retired in M4S-5699
    // when the DANI team took ownership of the row and de-flagged it;
    // `reports-llm-convo-signals` in M4S-5700, when the LLM Signals card went
    // live on a flagless row.) `reports-leakage`
    // was registered here for a row we authored and never shipped; it exists in
    // no environment and nothing reads it now.
    // No ai-crosstabs key: staff always have AI Crosstabs and clients get it with the switch
    // below (M4S-5777 X1, docs/pdlc/crosstabs-access-lifecycle.md). Nothing reads that flag.
    // Retires the classic crosstab tool (docs/pdlc/crosstabs-retire-legacy.md). ON: every link,
    // button and list row opens AI Crosstabs, AI Crosstabs is on for clients, a classic crosstab
    // opens as a conversion, and an old /crosstab/<key> address converts on open.
    // Unreadable resolves false, which changes nothing.
    LEGACY_CROSSTABS_RETIRED_FLAG,
    // Start a crosstab from a DANI chat (M4S-5777 X149–X151, docs/pdlc/crosstabs-dani.md). Also
    // needs DANI access, and staff only while DANI_LAUNCH_INTERNAL_ONLY; the server re-reads it.
    CROSSTABS_DANI_LAUNCH_FLAG,
    'studio-read-only',
    'reel-studio-transcript-export',
    'reel-studio-compare',
    // Branded intro slate on exported reels (M4S-5252, docs/pdlc/reel-intro-slate.md).
    // Turn on in an environment only after the API's render accepts `intro` (API-1):
    // with it on, every export sends the field.
    'reel-studio-intro-slate',
    // Gates the Reel Studio transcript paywall (M4S-5321). Must stay OFF for
    // customers until M4S-5561's backfill has run in production — enabling it
    // first strips transcripts from customers who already have them. Read
    // server-side by src/lib/transcript-access/server-feature-flag.ts, since
    // the withholding happens in the API route rather than in the UI.
    'transcript-access-gate',
    'studio-internal-nav',
    // Staff View-as rollout list (M4S-5670): per-user overrides only, never
    // enabledInternal, an org override or a group override, each of which would
    // turn View-as on for every staff member. Re-read server-side by
    // src/lib/view-as/rollout-flag.ts on every start, resume, check and action.
    'studio-view-as',
    // Reports as a client under View-as (M4S-5670 §7). Per-user overrides that
    // mirror the View-as list, and on in an environment only after Monahan
    // confirms in writing that API-1 (the planner forward) is deployed there.
    'studio-view-as-reports',
    // Client hierarchy switcher only (parent/subsidiary orgs); does not gate
    // staff View-as since M4S-5670.
    'studio-subsidiary',
    'spotlight-mining-of-insights',
    'insight-engine-email-deep-links',
    // Feasibility with DANI (M4S-5371). INTERNAL surfaces only, and it ships
    // enabledExternal=false: Capability O's customer-facing variant gets its own
    // key, because a flag is a rollout control and not the authorization
    // boundary (that is F-8.2). The legacy internal tool keeps its own separate
    // 'studio.nav.feasibility' nav flag — untouched.
    'feasibility-dani',
    // "Look into it further" on a feasibility check (draft Amendment 15). Granted
    // per user to power users; admins always have it. Server-enforced in
    // src/lib/feasibility/investigation/access-server.ts.
    'feasibility-investigate',
    // OE Coding MVP (M4A2A-471). Reserved for the client rollout; staff reach the
    // pages today through `internalHref` + INTERNAL_ROUTES, with no flag needed.
    'oe-coding',
    // Capability O (F-8, M4S-5543): the client self-serve survey-feasibility page
    // at /dani-feasibility. Its own key, off for every external org until F-8.12's
    // sign-off gate. The server-side audience gate in
    // src/app/api/studio/v1/dani-feasibility/ is the boundary, not this flag.
    'feasibility-client',
    // A70 Sales handoff (F-8.6, M4S-5550): when a client's check comes back
    // Marginal or Not feasible, post it to the Sales channel. Its own key so
    // Capability O can run with the handoff off. Read server-side by the
    // audience gate; off (or unreadable) = nothing is posted and the audit log
    // says so. Nothing about the handoff is ever shown to the client.
    'feasibility-handoff',
    // Slide Creator internal alpha (M4S-5702, docs/pdlc/slide-creator-alpha.md):
    // per-user overrides for the named testers, read with being internal, so it
    // never opens anything for a client. Replaces `report-generation`, which
    // gated only the My Surveys badge. Re-read server-side by every
    // /api/slide-creation route (src/lib/slide-creation/alpha-access-server.ts).
    SLIDE_CREATOR_ALPHA_FLAG,
    // Trial onboarding emails through HubSpot (M4S-5602). ON: Enroll on
    // /tools/trial-onboarding posts each trial user's payload to HubSpot, whose
    // workflow sends the four emails. OFF (and whenever the flag cannot be read):
    // the payload is recorded as a dry run and nothing leaves Studio. Read
    // server-side by src/lib/trial-onboarding/hubspot-flag.ts, as the admin who
    // pressed Enroll. Replaces the never-read `trial-onboarding-emails`.
    'hubspot-email-nurture',
    // Builder, the native survey builder (docs/pdlc/builder.md, B5). ON: /builder
    // renders the native editor. OFF (and whenever the flag cannot be read):
    // /builder renders today's MFour Services iframe, unchanged.
    'survey-builder-native',
    // The developer portal's staff page (/tools/dev-portal, docs/pdlc/builder-api.md):
    // a view of the protocol Builder, agents and outside clients call. OFF (and
    // whenever the flag cannot be read): the page and its Tools menu entry are
    // hidden. Addressed in a later release; the API itself does not read it.
    'dev-portal',
    // The staff tools that retire SOTG's control panel (Builder draft Amendment 1, R1,
    // docs/pdlc/builder-amendment-1-sotg-retirement.md): one per-person flag per tool, re-read
    // on the server by src/lib/staff-tools/gate.ts. Unreadable resolves false.
    ...STAFF_TOOL_FLAGS,
    // Native Studio dashboards (M4S-5280), one flag per dashboard. ON: the route
    // renders the native build instead of the Sigma iframe. OFF, missing or
    // unreadable: Sigma, exactly as today. Create the backend record per
    // dashboard only after its cutover gate passes (native-dashboards-prd.md §7a).
    'dashboards-native-visitor-trends',
    'dashboards-native-app',
    'dashboards-native-location',
    'dashboards-native-website',
    'dashboards-native-abc-norms',
    'dashboards-native-burlington-vtracker',
    'dashboards-native-cargurus-app-and-web-visitors-vtracker',
    'dashboards-native-nacs-vtracker-plus',
    'dashboards-native-iooh-brand-lift',
    // Observe mode (M4S-5475). Each key only decides whether that surface calls
    // the charge path. Zero-rate and enforcement stay on the API registry row.
    // Absent or unreadable resolves false, so nothing is metered until an override grants it.
    'credits-observe-transcription',
    'credits-observe-data-export',
    'credits-observe-crosstab-build',
    'credits-observe-pptx-export',
    'credits-observe-video',
    // The staff fleet on the new balance and the Rate Card's DANI conversion changes (M4S-5895,
    // plan step 29). Per-user overrides, staff only; nothing a client sees reads it. Shipped
    // without this line, so it read off for everyone whatever its overrides said.
    FLEET_BALANCE_FLAG,
    ...STUDIO_NAV_FEATURE_FLAG_KEYS,
];

/**
 * Keys used only for QA verification of gating when APP_ENV is not production
 * (`NEXT_PUBLIC_APP_ENV`). NODE_ENV may still be `production` in test/staging builds.
 * Create matching flags in the admin tool (or backend) before testing.
 */
export const FEATURE_FLAG_QA_VERIFICATION_KEYS = [
    'qa.gate.demo-a',
    'qa.gate.demo-b',
    'qa.gate.demo-c',
] as const;

export type FeatureFlagQaVerificationKey = (typeof FEATURE_FLAG_QA_VERIFICATION_KEYS)[number];

export function isFeatureFlagsQaDashboardEnabled(): boolean {
    return !isAppEnvProduction();
}

/**
 * The full set of keys `FeatureFlagsProvider` fetches on app load. Shared by
 * the client provider (src/providers/index.tsx) and the root layout's
 * server-side prefetch (src/app/layout.tsx) so both ask the backend about
 * exactly the same flags — the prefetch's whole purpose is to seed the
 * client query with a value it agrees with, and that only holds if the key
 * lists never drift apart.
 */
export const FEATURE_FLAG_PROVIDER_KEYS: string[] = [
    ...FEATURE_FLAG_KEYS,
    ...(isFeatureFlagsQaDashboardEnabled() ? [...FEATURE_FLAG_QA_VERIFICATION_KEYS] : []),
];
